import type { CaptureSequence, CustomLandmarks, Landmark } from '@/components/posture-analysis/types';
import type { PostureClassificationStrategyContext } from './posture-orientation-strategy-types';

const MIN_PX = 18;

const rad2deg = (r: number) => (r * 180) / Math.PI;

/**
 * Builds optional ASIS–PSIS line in image pixels for the active lateral view when pins exist.
 * Mirrors analyzeLateral swap when ASIS/PSIS appear reversed for the view side.
 */
export function buildAsisPsisLineForSide(
  side: 'Left' | 'Right',
  custom?: CustomLandmarks
): PostureClassificationStrategyContext['asisPsisLine'] {
  if (!custom?.ASIS || !custom?.PSIS) return null;
  let asis = custom.ASIS;
  let psis = custom.PSIS;
  const expectedAnteriorSign = side === 'Right' ? 1 : -1;
  const rawDx = asis.x - psis.x;
  if (rawDx * expectedAnteriorSign < 0) {
    const tmp = asis;
    asis = psis;
    psis = tmp;
  }
  if (Math.abs(asis.x - psis.x) < MIN_PX && Math.abs(asis.y - psis.y) < MIN_PX) return null;
  return { a: { x: asis.x, y: asis.y }, b: { x: psis.x, y: psis.y } };
}

export function inferSagittalInputFromCaptured(
  captured: Partial<
    Record<
      CaptureSequence,
      { landmarks?: Landmark[] | null; cmPerPx?: number; custom?: CustomLandmarks } | null
    >
  >
): {
  sagittal: import('./posture-orientation-strategy-types').PostureStrategySagittalLandmarks | null;
  side: 'Left' | 'Right' | null;
} {
  const preferRight = captured.Right?.landmarks && captured.Right?.cmPerPx;
  const preferLeft = captured.Left?.landmarks && captured.Left?.cmPerPx;
  const side: 'Left' | 'Right' | null = preferRight ? 'Right' : preferLeft ? 'Left' : null;
  if (!side) return { sagittal: null, side: null };
  const pack = captured[side];
  if (!pack?.landmarks || !pack.cmPerPx) return { sagittal: null, side };
  return {
    sagittal: {
      sideView: side,
      landmarks: pack.landmarks,
      cmPerPx: pack.cmPerPx,
      custom: pack.custom,
    },
    side,
  };
}

export function pelvicTiltDegFromLineAndSide(
  line: { a: { x: number; y: number }; b: { x: number; y: number } },
  side: 'Left' | 'Right'
): number {
  const dy = line.a.y - line.b.y;
  const dx = Math.abs(line.a.x - line.b.x);
  if (dx < 1e-6 && Math.abs(dy) < 1e-6) return NaN;
  let tilt = rad2deg(Math.atan2(dy, dx));
  return tilt;
}
